import { FormData } from './schema';
import OpenAI from 'openai';
import Airtable from 'airtable';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

const base = new Airtable({ 
  apiKey: import.meta.env.VITE_AIRTABLE_API_KEY 
}).base(import.meta.env.VITE_AIRTABLE_BASE_ID);

let currentRecordId: string | null = null;

export async function generatePDF(element: HTMLElement): Promise<string> {
  try {
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'px',
      format: [canvas.width, canvas.height]
    });

    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    const pdfBlob = pdf.output('blob');
    return URL.createObjectURL(pdfBlob);
  } catch (error) {
    console.error('PDF generation error:', error);
    throw error;
  }
}

export async function generateProposal(data: FormData): Promise<string> {
  try {
    const thread = await openai.beta.threads.create();
    
    await openai.beta.threads.messages.create(thread.id, {
      role: "user",
      content: `Generate a professional project proposal based on the following information:
      
Client: ${data.firstName} ${data.lastName}
Company: ${data.company}
Business Description: ${data.businessDescription}
Problem: ${data.problem}
Proposed Solution: ${data.solution}
Platforms/Services: ${data.platforms}
Project Duration: ${data.projectDuration}
Project Cost: ${data.budget}
Additional Costs: ${data.additionalCosts || 'None'}
Call Outcome: ${data.callOutcome}

Please format the Timeline/Milestones section in a table format like this:
| Milestone | Timeline |
| [Milestone 1] | [Duration] |
| [Milestone 2] | [Duration] |`
    });

    const run = await openai.beta.threads.runs.create(
      thread.id,
      { assistant_id: "asst_zZgqMon37e4nm1A9mMyFc00S" }
    );

    while (true) {
      const runStatus = await openai.beta.threads.runs.retrieve(
        thread.id,
        run.id
      );
      
      if (runStatus.status === 'completed') {
        break;
      } else if (runStatus.status === 'failed') {
        throw new Error('Failed to generate proposal');
      }
      
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    const messages = await openai.beta.threads.messages.list(thread.id);
    const lastMessage = messages.data[0];
    
    if (!lastMessage || !lastMessage.content[0] || lastMessage.content[0].type !== 'text') {
      throw new Error('No proposal generated');
    }

    return lastMessage.content[0].text.value;
  } catch (error) {
    console.error('API error:', error);
    throw error;
  }
}

export async function storeProposal(data: FormData, proposal: string, pdfUrl?: string) {
  try {
    const budgetValue = data.budget.replace(/[^0-9.]/g, '');
    const numericBudget = budgetValue ? parseFloat(budgetValue) : 0;

    const fields = {
      "fldLKYMcmnAdS2LB1": data.firstName,
      "fldyt7BOPtT4DmoDJ": data.lastName,
      "fldxz69wlmuANTQho": data.email,
      "fldIcWH6qxpDKQ6zB": data.company,
      "fldOpVqHsoEgxfDU2": data.businessDescription,
      "fldTj3wqlItq4PXJF": data.callOutcome,
      "fldywiHIH5327gfut": numericBudget,
      "fldr8ZXgOQL7VzSAL": data.projectDuration,
      "fldnikY2Kng5PZEf9": data.problem,
      "fldpY5rkw0cdd6Ij0": data.solution,
      "fldjKF1TAYRUOZUxa": data.platforms,
      "fld34Xg0nuV0hWk3N": pdfUrl || '',
      [import.meta.env.VITE_AIRTABLE_FIELD_ID]: JSON.stringify({
        additionalCosts: data.additionalCosts,
        proposal: proposal,
        timestamp: new Date().toISOString()
      })
    };

    if (currentRecordId) {
      const record = await base(import.meta.env.VITE_AIRTABLE_TABLE_ID).update(currentRecordId, fields);
      return record;
    } else {
      const record = await base(import.meta.env.VITE_AIRTABLE_TABLE_ID).create(fields);
      currentRecordId = record.id;
      return record;
    }
  } catch (error) {
    console.error('Airtable error:', error);
    throw error;
  }
}